/**
 * DashboardFocusShell — PART C (Dashboard Simplification, Feb 2026)
 *
 * Wraps an existing role dashboard with a calmer, role-aware default
 * landing experience:
 *
 *   FOCUS mode (default)  → shows the role's `<*FocusPanel>` with
 *                            primary action + top action-item cards.
 *                            Existing dashboard is collapsed behind
 *                            a single "Show full dashboard" expander.
 *
 *   EVERYTHING mode      → renders the existing dashboard JSX as-is.
 *                          Nothing about the legacy view is changed —
 *                          this shell just gets out of its way.
 *
 * Mode is persisted per-role in `localStorage` (`cn:dashboard:mode:<role>`),
 * so a power-user who prefers everything-mode never sees the focus view
 * again. New users get focus mode on first visit.
 *
 * Rule Zero: this component is ADDITIVE. It does not modify the wrapped
 * dashboard's props, state, or render output. The wrapped dashboard
 * continues to work standalone if mounted directly without the shell.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Sparkles, ChevronDown, ChevronRight, Layers, Loader2, ArrowRight } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { ThemeToggle } from '../brand/ThemeToggle';
import { PlannerFocusPanel } from './PlannerFocusPanel';
import { ClinicianFocusPanel } from './ClinicianFocusPanel';
import { AgencyFocusPanel } from './AgencyFocusPanel';
import { CaraNudges } from '../brand/CaraNudges';
import { AdminFocusPanel } from './AdminFocusPanel';
import { PatientFocusPanel } from './PatientFocusPanel';
import { PayerFocusPanel } from './PayerFocusPanel';
import { FocusHeaderChip } from './FocusHeaderChip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FOCUS_PANELS = {
  planner: PlannerFocusPanel,
  clinician: ClinicianFocusPanel,
  agency: AgencyFocusPanel,
  agency_admin: AgencyFocusPanel,
  admin: AdminFocusPanel,
  patient: PatientFocusPanel,
  family: PatientFocusPanel,
  caregiver: PatientFocusPanel,
  payer: PayerFocusPanel,
};

const modeKey = (role) => `cn:dashboard:mode:${role || 'unknown'}`;

export function DashboardFocusShell({ role, children }) {
  const { token } = useAuth();
  const [mode, setModeState] = useState(() => {
    try {
      return localStorage.getItem(modeKey(role)) || 'focus';
    } catch {
      return 'focus';
    }
  });
  const [legacyOpen, setLegacyOpen] = useState(false);
  const [focus, setFocus] = useState(null);
  const [loading, setLoading] = useState(true);

  const setMode = useCallback((next) => {
    setModeState(next);
    try {
      localStorage.setItem(modeKey(role), next);
    } catch {
      // ignore quota / private mode
    }
  }, [role]);

  const loadFocus = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/dashboard/focus`,
        { headers: { Authorization: `Bearer ${token}` } });
      setFocus(res.data || null);
    } catch {
      setFocus(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadFocus(); }, [loadFocus]);

  const Panel = FOCUS_PANELS[role] || null;

  // EVERYTHING mode → render the wrapped dashboard as-is with a slim
  // top bar to switch back to focus.
  if (mode === 'everything') {
    return (
      <div data-testid={`dashboard-focus-shell-${role}`} data-mode="everything">
        <ModeBar role={role} mode={mode} setMode={setMode} />
        <div className="max-w-6xl mx-auto px-6 pt-6">
          <CaraNudges variant="row" />
        </div>
        {children}
      </div>
    );
  }

  // FOCUS mode → calm panel + a collapsible "Show full dashboard"
  return (
    <div data-testid={`dashboard-focus-shell-${role}`} data-mode="focus">
      <ModeBar role={role} mode={mode} setMode={setMode} />

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        {/* Cara nudges — proactive role-scoped suggestions from real DB
            reads. Surfaced ABOVE the focus panel so they're the first
            thing the user sees on every dashboard load. Dismissible. */}
        <CaraNudges variant="row" />

        {loading ? (
          <div className="py-12 flex items-center justify-center text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Reading your priority queue…
          </div>
        ) : Panel ? (
          <Panel focus={focus} role={role} onReload={loadFocus} />
        ) : (
          <GenericFocusFallback focus={focus} />
        )}

        {/* Legacy dashboard, collapsed by default */}
        <div className="border-t border-slate-100 pt-6"
              data-testid="dashboard-legacy-collapsible">
          <button type="button"
                  onClick={() => setLegacyOpen((v) => !v)}
                  className="w-full flex items-center justify-between gap-3 px-5 py-4 rounded-2xl bg-white border border-slate-200 hover:border-slate-300 transition-colors"
                  data-testid="dashboard-legacy-toggle"
                  aria-expanded={legacyOpen}>
            <span className="flex items-center gap-3">
              <Layers className="w-5 h-5 text-slate-500" />
              <span className="text-sm font-medium text-slate-900">
                {legacyOpen ? 'Hide full operations dashboard' : 'Show full operations dashboard'}
              </span>
            </span>
            <ChevronDown
              className={`w-5 h-5 text-slate-400 transition-transform ${legacyOpen ? 'rotate-180' : ''}`}
            />
          </button>
        </div>
      </div>

      {/* Iter 158.2 · Mount children unconditionally (visually hidden
          in focus mode) so any deep-link listeners + portal-based
          modals owned by the wrapped dashboard (e.g. Planner's
          ?refer=1 → <ReferralModal/>) stay reachable regardless of
          the legacy expander state. The dashboard's Dialog components
          use Radix portals — they render at document.body, so the
          modal still overlays the focus panel correctly. */}
      <div
        data-testid="dashboard-legacy-content"
        data-legacy-open={legacyOpen ? 'true' : 'false'}
        className="border-t border-slate-100 mt-0"
        style={legacyOpen ? undefined : { display: 'none' }}
      >
        {children}
      </div>
    </div>
  );
}

function ModeBar({ role, mode, setMode }) {
  return (
    <div className="border-b border-slate-100 bg-white sticky top-0 z-30"
          data-testid="dashboard-mode-bar">
      <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between gap-3">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-medium flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5" /> {mode === 'focus' ? "Today's focus" : 'Full operations view'}
        </div>
        <div className="inline-flex items-center gap-2">
          <div className="inline-flex items-center gap-1 p-1 rounded-full bg-slate-100"
                role="tablist" aria-label="Dashboard mode">
            <button type="button" role="tab"
                    aria-selected={mode === 'focus'}
                    onClick={() => setMode('focus')}
                    data-testid="dashboard-mode-focus"
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                      mode === 'focus'
                        ? 'bg-white shadow-sm text-slate-900'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}>
              Focus
            </button>
            <button type="button" role="tab"
                    aria-selected={mode === 'everything'}
                    onClick={() => setMode('everything')}
                    data-testid="dashboard-mode-everything"
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                      mode === 'everything'
                        ? 'bg-white shadow-sm text-slate-900'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}>
              Everything
            </button>
          </div>
          <FocusHeaderChip role={role} />
          <ThemeToggle />
        </div>
      </div>
    </div>
  );
}

function GenericFocusFallback({ focus }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center"
          data-testid="dashboard-focus-generic">
      <Sparkles className="w-7 h-7 text-slate-300 mx-auto mb-3" />
      <h2 className="text-2xl font-semibold text-slate-900 mb-1">
        Nothing demands your attention right now
      </h2>
      <p className="text-sm text-slate-500">
        Open the full dashboard below to dive into the details.
      </p>
      {focus?.primary_action && (
        <Link to={focus.primary_action.href}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 mt-5"
            data-testid="dashboard-focus-primary-action">
          {focus.primary_action.label} <ArrowRight className="w-4 h-4" />
        </Link>
      )}
    </div>
  );
}

/* Re-export so consumers can use this single import surface. */
export { ChevronRight };
